import emergencyResponseTeamMtxApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_mtx';
import type {
  EmergencyResponseTeamReportWithPhotos,
  EmergencyResponseTeamReportExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import type {
  CarInfo,
  SearchConditions,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import type {
  EmergencyResponseTeamCarExt,
  EmergencyResponseTeamCarShow,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamCar';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';

const maxDateRange = 31;
const oneDayMsec = 1000 * 60 * 60 * 24;

interface ConvDataParams {
  cars: EmergencyResponseTeamCarShow[];
  currentCarInfoMap: Partial<Record<number, CarInfo>>;
  userSettings: ExternalReportSettings;
}
export type CurrentReportItemInfo = Pick<
  CarInfo,
  'selectedPhotoId' | 'selectedMtxTs' | 'savedImageMap' | 'loadedMtxs'
>;

export function initSearchConds(): SearchConditions {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return {
    areaId: null,
    companyId: null,
    companyIds: [],
    hanName: null,
    status: '全て',
    dateFrom: new Date(now.getTime() - oneDayMsec * maxDateRange),
    dateTo: now,
  };
}

export function validSearchDateRange(searchConds: SearchConditions): boolean {
  const from = searchConds.dateFrom.getTime();
  const to = searchConds.dateTo.getTime();
  return Number.isFinite(from) && Number.isFinite(to) &&
    from <= to && (to - from) <= maxDateRange * oneDayMsec;
}

export function createInitialCarsData(recentData: EmergencyResponseTeamCarShow[]): EmergencyResponseTeamCarExt[] {
  return recentData.map(car => {
    return {
      ...car,
      isMoving: false,
      isWorking: false,
      shouldShow: true,
      isSelected: false,
      pos: null,
      reportExt: null,
    };
  });
}

export function convData(params: ConvDataParams): EmergencyResponseTeamCarExt[] {
  return params.cars.map(car => {
    let reportExt: EmergencyResponseTeamReportExt | null = null;
    const tmpCar = params.currentCarInfoMap[car.id];
    if (car.report) {
      const itemInfo = tmpCar ? {
        selectedPhotoId: tmpCar.selectedPhotoId,
        selectedMtxTs: tmpCar.selectedMtxTs,
        savedImageMap: tmpCar.savedImageMap,
        loadedMtxs: tmpCar.loadedMtxs,
      } : undefined;
      reportExt = convReport(car.report, itemInfo);
    }
    const shouldShow = true;
    const statusChoices = params.userSettings.choices.status;
    const isWorking = car.status === statusChoices.working.key;
    // tsIsNewがfalseの場合は、停止中とみなす
    const isMoving = car.tsIsNew && isWorking;
    const isSelected = tmpCar?.isSelected ?? false;

    // posはLayerManagerで変換して設定する
    return {
      ...car,
      isMoving,
      isWorking,
      shouldShow,
      isSelected,
      pos: null,
      reportExt,
    };
  });
}

export function shouldShowCar(
  car: EmergencyResponseTeamCarExt,
  decidedSearchConds: SearchConditions,
): boolean {
  // 検索条件に照らして車を表示すべきか判定
  if (decidedSearchConds.companyIds.length > 0 &&
    !decidedSearchConds.companyIds.includes(Number(car.companyId))
  ) {
    return false;
  }
  if (decidedSearchConds.hanName &&
    car.hanName !== decidedSearchConds.hanName) {
    return false;
  }
  if (decidedSearchConds.status !== '' && decidedSearchConds.status !== '全て') {
    if (car.status !== decidedSearchConds.status) {
      return false;
    }
  }

  return true;
}

export const convReport = (
  report: EmergencyResponseTeamReportWithPhotos,
  currentReportItemInfo?: CurrentReportItemInfo,
): EmergencyResponseTeamReportExt => {
  const loadedMtxs = currentReportItemInfo?.loadedMtxs || [];
  const reportExt: EmergencyResponseTeamReportExt = {
    ...report,
    photosExt: report.photos.map(photo => ({
      ...photo,
      isSelected: currentReportItemInfo?.selectedPhotoId === photo.id,
      hanName: report.hanName ?? '',
      companyName: report.companyName ?? '',
      savedImage: null,
      samePosPhotos: [],
    })),
    mtxsExt: loadedMtxs,
    isSelected: false,
    isMtxsLoaded: loadedMtxs.length > 0,
  };
  for (const photo of reportExt.photosExt) {
    // 取得済みの画像は再取得しない
    photo.savedImage = currentReportItemInfo?.savedImageMap?.[photo.id] ?? null;
    photo.samePosPhotos = reportExt.photosExt.filter(
      (e) => e.lat === photo.lat && e.lon === photo.lon,
    );
  }
  return reportExt;
};

export const loadEmergencyResponseTeamMtxs = async(
  reports: EmergencyResponseTeamReportExt[],
): Promise<void> => {
  try {
    const emergencyResponseTeamMtxs = await emergencyResponseTeamMtxApi.getMtxsByReportIds({
      report_ids: reports.map(report => report.id),
    });
    reports.forEach(report => {
      if (!emergencyResponseTeamMtxs[report.id]) return;
      report.mtxsExt = emergencyResponseTeamMtxs[report.id].map(mtx => {
        return {
          ...mtx,
          isSelected: false,
          hanName: report.hanName,
          companyName: report.companyName ?? '',
        };
      });
      report.isMtxsLoaded = true;
    });
  } catch (e) {
    console.error('loadEmergencyResponseTeamMtxs failed', e);
  }
};

export const deselectReportItems = (report: EmergencyResponseTeamReportExt): void => {
  report.photosExt.forEach(photo => {
    photo.isSelected = false;
  });
  report.mtxsExt.forEach(mtx => {
    mtx.isSelected = false;
  });
};

export const convSearchParamTsTo = (dateTo: Date): Date => {
  return new Date(dateTo.getTime() + oneDayMsec - 1);
};
